import moment from "moment-timezone";

export const hourOptions = Array.from({ length: 6 }, (_, i) => ({
  value: i.toString(),
  label: `${i}hr`,
}));

export const minuteOptions = Array.from({ length: 12 }, (_, i) => ({
  value: (i * 5).toString(),
  label: `${i * 5}min`,
}));

/**
 * Generate duration options from 5 to 240 minutes in 5-minute increments
 * Formats as "X hrs Y min" for better readability
 * @returns {Array<{value: string, label: string}>} Duration options
 */
export const generateDurationOptions = () => {
  const options = [];
  for (let minutes = 5; minutes <= 240; minutes += 5) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const label =
      hours > 0
        ? `${hours} hr${hours > 1 ? "s" : ""}${mins > 0 ? ` ${mins} min` : ""}`
        : `${mins} min`;
    options.push({ value: `${minutes}`, label });
  }
  return options;
};

// Calculate individual duration components and end time from staff service
export const calculateDurationAndEndTime = (
  services,
  staffId,
  serviceId,
  startTime,
  currentFormData = null,
) => {
  if (!staffId || !serviceId) return null;

  const selectedService = services.find((s) => s.id === serviceId);
  if (!selectedService) return null;

  const staffService = selectedService.servicing_staff.find(
    (s) => s.staff_id === staffId,
  );
  if (!staffService) return null;

  // Get individual duration components from staff service
  const baseDuration =
    staffService.base_duration_mins ?? staffService.duration_mins ?? 30;
  const processingTime = staffService.processing_time_mins ?? 0;
  const finishingTime = staffService.finishing_time_mins ?? 0;
  const bufferTime = staffService.buffer_time_mins ?? 0;

  // Calculate total duration and end time
  const totalDuration =
    baseDuration + processingTime + finishingTime + bufferTime;
  const newEndTime = startTime
    ? moment(startTime, "HH:mm").add(totalDuration, "minutes").format("HH:mm")
    : "";

  return {
    baseDuration,
    processingTime,
    finishingTime,
    bufferTime,
    endTime: newEndTime,
  };
};

export const calculateEndTime = (
  services,
  startTime,
  salonServiceId,
  customDurations = null,
) => {
  if (!startTime) return "";

  let totalDuration;
  if (customDurations) {
    // If custom durations provided, sum them up
    totalDuration =
      (customDurations.baseDuration ?? 0) +
      (customDurations.processingTime ?? 0) +
      (customDurations.finishingTime ?? 0) +
      (customDurations.bufferTime ?? 0);
  } else {
    const service = services.find((s) => s.id === salonServiceId);
    if (!service) return "";

    // Use default service durations
    const baseDuration = service.default_base_duration_mins ?? 0;
    const processingTime = service.default_processing_time_mins ?? 0;
    const finishingTime = service.default_finishing_time_mins ?? 0;
    const bufferTime = service.default_buffer_time_mins ?? 0;
    totalDuration = baseDuration + processingTime + finishingTime + bufferTime;
  }

  const endTime = moment(startTime, "HH:mm")
    .add(totalDuration, "minutes")
    .format("HH:mm");

  return endTime;
};

/**
 * Build appointment start instant for the API.
 *
 * Mantine v8's DateInput emits "YYYY-MM-DD" strings, which must be treated as
 * the literal calendar day the user selected. Passing such a string to
 * `new Date(...)` parses it as UTC midnight and shifts to the previous day in
 * negative-offset zones; instead, take the date portion of the string as-is.
 * For legacy Date values (e.g. pre-filled from a CalendarEvent), read Y/M/D in
 * browser-local time. The resulting calendar day + start time are then
 * interpreted as wall time in the salon's `timeZone`.
 */
function buildStartDateTime(formData, timeZone) {
  const zone =
    typeof timeZone === "string" && timeZone.trim().length > 0
      ? timeZone.trim()
      : "America/New_York";

  if (!formData?.date || !formData?.startTime) {
    return new Date();
  }

  let datePart;
  if (typeof formData.date === "string") {
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(formData.date);
    if (!match) {
      return new Date();
    }
    datePart = match[1];
  } else if (formData.date instanceof Date) {
    if (Number.isNaN(formData.date.getTime())) {
      return new Date();
    }
    const y = formData.date.getFullYear();
    const mo = formData.date.getMonth() + 1;
    const da = formData.date.getDate();
    datePart = `${y}-${String(mo).padStart(2, "0")}-${String(da).padStart(2, "0")}`;
  } else {
    return new Date();
  }

  const timeM = moment(formData.startTime, "HH:mm", true);
  if (!timeM.isValid()) {
    return new Date();
  }
  const timePart = timeM.format("HH:mm");

  const combined = moment.tz(
    `${datePart} ${timePart}`,
    "YYYY-MM-DD HH:mm",
    zone,
  );

  if (!combined.isValid()) {
    return moment
      .tz(`${datePart} ${timePart}`, "YYYY-MM-DD HH:mm", "America/New_York")
      .seconds(0)
      .milliseconds(0)
      .toDate();
  }

  return combined.seconds(0).milliseconds(0).toDate();
}

export const handleSubmitHelper = (
  formData,
  staffServices,
  services,
  selectedAdjustmentOptions = [],
  timeZone,
) => {
  const startDateTime = buildStartDateTime(formData, timeZone);

  // Use the duration values from formData (user can modify these)
  const baseDuration = formData.baseDuration ?? 30;
  const processingTime = formData.processingTime ?? 0;
  const finishingTime = formData.finishingTime ?? 0;
  const bufferTime = formData.bufferTime ?? 0;

  // Create-only: the legacy forms no longer edit appointments (reschedule is
  // the booking drawer / drag-and-drop, both via editTransactionLineItems).
  return {
    booked_services: [
      {
        staff_service_id: staffServices.find(
          (service) =>
            service.staffId === formData.employeeId &&
            service.salonServiceId === formData.salonServiceId,
        )?.id,
        start_dt: startDateTime,
        base_duration_mins: baseDuration,
        processing_time_mins: processingTime,
        finishing_time_mins: finishingTime,
        buffer_time_mins: bufferTime,
        // Include service adjustment form option IDs if any are selected
        ...(selectedAdjustmentOptions.length > 0 && {
          service_adjustment_form_option_ids: selectedAdjustmentOptions,
        }),
      },
    ],
    // Walk-ins no longer come through here: they are booked from the
    // waitlist (POST /waitlist/{uuid}/assign sets the walk-in source).
    source: "Manually input",
    client_uuid: formData.clientUuid,
  };
};
